import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth, api } from '../App';
import { toast } from 'sonner';
import Sidebar from '../components/layout/Sidebar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Mic,
  MicOff,
  Phone,
  PhoneOff,
  User,
  Building2,
  DollarSign,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  RefreshCw,
  Save,
  UserPlus,
  Volume2,
  VolumeX,
  Sparkles,
  ArrowRight,
  History,
  Plus,
  UserCircle,
  Briefcase,
  Heart,
  Settings2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// WebRTC Realtime Audio Client
class RealtimeAudioClient {
  constructor(onMessage, onStatusChange, onTranscript) {
    this.peerConnection = null;
    this.dataChannel = null;
    this.audioElement = null;
    this.localStream = null;
    this.onMessage = onMessage;
    this.onStatusChange = onStatusChange;
    this.onTranscript = onTranscript;
    this.fullTranscript = '';
  }

  async init(personalityConfig) {
    try {
      this.onStatusChange('connecting');
      
      // Get session from backend
      const tokenResponse = await fetch(`${BACKEND_URL}/api/realtime/session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!tokenResponse.ok) {
        throw new Error("Failed to get session token");
      }
      
      const data = await tokenResponse.json();
      if (!data.client_secret?.value) {
        throw new Error("Invalid session response");
      }

      // Create WebRTC peer connection
      this.peerConnection = new RTCPeerConnection();
      this.setupAudioElement();
      await this.setupLocalAudio();
      this.setupDataChannel(personalityConfig);

      // Create and send offer
      const offer = await this.peerConnection.createOffer();
      await this.peerConnection.setLocalDescription(offer);

      // Send offer to backend and get answer
      const response = await fetch(`${BACKEND_URL}/api/realtime/negotiate`, {
        method: "POST",
        body: offer.sdp,
        headers: {
          "Content-Type": "application/sdp",
          "Authorization": `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error("Negotiation failed");
      }

      const { sdp: answerSdp } = await response.json();
      const answer = {
        type: "answer",
        sdp: answerSdp
      };

      await this.peerConnection.setRemoteDescription(answer);
      this.onStatusChange('connected');
      console.log("WebRTC connection established");
      
    } catch (error) {
      console.error("Failed to initialize audio client:", error);
      this.onStatusChange('error');
      throw error;
    }
  }

  setupAudioElement() {
    this.audioElement = document.createElement("audio");
    this.audioElement.autoplay = true;
    document.body.appendChild(this.audioElement);

    this.peerConnection.ontrack = (event) => {
      this.audioElement.srcObject = event.streams[0];
    };
  }

  async setupLocalAudio() {
    this.localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.localStream.getTracks().forEach(track => {
      this.peerConnection.addTrack(track, this.localStream);
    });
  }

  setupDataChannel(personalityConfig) {
    this.dataChannel = this.peerConnection.createDataChannel("oai-events");
    
    this.dataChannel.onopen = () => {
      console.log("Data channel opened");
      // Send session configuration with personality
      if (personalityConfig) {
        this.sendSessionConfig(personalityConfig);
      }
    };
    
    this.dataChannel.onmessage = (event) => {
      console.log("Received event:", event.data);
      try {
        const data = JSON.parse(event.data);
        this.handleRealtimeEvent(data);
      } catch (e) {
        console.error("Failed to parse event:", e);
      }
    };
  }

  sendSessionConfig(personalityConfig) {
    // Configure the session with our loan intake instructions
    const sessionUpdate = {
      type: "session.update",
      session: {
        modalities: ["text", "audio"],
        voice: personalityConfig?.voice || "alloy",
        instructions: personalityConfig?.instructions || this.getDefaultInstructions(),
        input_audio_format: "pcm16",
        output_audio_format: "pcm16",
        input_audio_transcription: {
          model: "whisper-1"
        },
        turn_detection: {
          type: "server_vad",
          threshold: 0.5,
          prefix_padding_ms: 300,
          silence_duration_ms: 500
        },
        temperature: 0.8
      }
    };
    
    this.sendMessage(sessionUpdate);
    console.log("Session configured with personality:", personalityConfig?.name, "voice:", sessionUpdate.session.voice);
  }

  getDefaultInstructions() {
    return `You are Sarah, a loan intake specialist at Great Leaf Lending. You're helping a potential borrower get started with their loan inquiry.

YOUR CONVERSATION FLOW:
1. Greet warmly and ask for their name
2. Ask for contact number
3. Ask for email
4. Ask about the property they're looking at - address, type, price
5. Ask about expected rental income
6. Ask what type of loan they're interested in (DSCR, bridge, etc.)
7. Ask how much they're looking to borrow
8. Ask about their credit score range

CONVERSATION STYLE:
- Professional but warm and personable
- Ask one question at a time, listen, then continue naturally
- Acknowledge what they share - "Great," "Got it," "Perfect"
- Keep responses conversational but concise (1-2 sentences)
- Guide them through like a helpful conversation, not an interrogation

You're here to make this easy for them. Be natural and helpful.`;
  }

  handleRealtimeEvent(event) {
    // Handle different event types from OpenAI Realtime API
    switch (event.type) {
      case 'session.created':
        console.log("Session created successfully");
        break;
        
      case 'session.updated':
        console.log("Session updated successfully");
        break;
        
      case 'conversation.item.input_audio_transcription.completed':
        // User's speech transcribed
        if (event.transcript) {
          this.fullTranscript += `\nUser: ${event.transcript}`;
          this.onTranscript(this.fullTranscript, event.transcript, 'user');
        }
        break;
        
      case 'response.audio_transcript.done':
        // AI's response transcribed
        if (event.transcript) {
          this.fullTranscript += `\nAssistant: ${event.transcript}`;
          this.onTranscript(this.fullTranscript, event.transcript, 'assistant');
        }
        break;
        
      case 'response.done':
        // Response completed - good time to extract data
        this.onMessage({ type: 'response_complete', transcript: this.fullTranscript });
        break;
        
      case 'error':
        console.error("Realtime API error:", event.error);
        this.onMessage({ type: 'error', error: event.error });
        break;
        
      default:
        // Log other events for debugging
        if (event.type) {
          console.log("Realtime event:", event.type);
        }
    }
  }

  sendMessage(message) {
    if (this.dataChannel && this.dataChannel.readyState === 'open') {
      this.dataChannel.send(JSON.stringify(message));
    }
  }

  disconnect() {
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop());
    }
    if (this.peerConnection) {
      this.peerConnection.close();
    }
    if (this.audioElement) {
      this.audioElement.remove();
    }
    this.onStatusChange('disconnected');
    return this.fullTranscript;
  }

  toggleMute(muted) {
    if (this.localStream) {
      this.localStream.getAudioTracks().forEach(track => {
        track.enabled = !muted;
      });
    }
  }
  
  getTranscript() {
    return this.fullTranscript;
  }
}

const VoiceIntakePage = () => {
  const { user } = useAuth();
  
  // Session state
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [realtimeAvailable, setRealtimeAvailable] = useState(false);
  
  // Voice client state
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // disconnected, connecting, connected, error
  const [isMuted, setIsMuted] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const audioClientRef = useRef(null);
  
  // Personality state
  const [personalities, setPersonalities] = useState([]);
  const [selectedPersonality, setSelectedPersonality] = useState(null);
  const [showPersonalityDialog, setShowPersonalityDialog] = useState(false);
  
  // Data state
  const [extractedData, setExtractedData] = useState({
    borrower: {},
    property: {},
    loan: {}
  });
  const [decision, setDecision] = useState(null);
  const [transcript, setTranscript] = useState('');
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showNewSessionDialog, setShowNewSessionDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  
  // Form state for manual data entry
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');

  useEffect(() => {
    loadInitialData();
    return () => {
      if (audioClientRef.current) {
        audioClientRef.current.disconnect();
      }
    };
  }, []);

  const loadInitialData = async () => {
    try {
      const [statusRes, sessionsRes, personalitiesRes] = await Promise.all([
        api.get('/intake/realtime-status'),
        api.get('/intake/sessions?limit=10'),
        api.get('/intake/personalities')
      ]);
      
      setRealtimeAvailable(statusRes.data.available);
      setSessions(sessionsRes.data);
      setPersonalities(personalitiesRes.data);
      
      // Set default personality (Sarah Professional)
      const defaultPersonality = personalitiesRes.data.find(p => p.id === 'sarah_pro') || personalitiesRes.data[0];
      setSelectedPersonality(defaultPersonality);
      
      // Check for active session
      const activeSession = sessionsRes.data.find(s => s.status === 'active');
      if (activeSession) {
        await loadSession(activeSession.session_id);
      }
    } catch (error) {
      console.error('Failed to load initial data:', error);
      toast.error('Failed to load intake data');
    } finally {
      setLoading(false);
    }
  };

  const loadSession = async (sessionId) => {
    try {
      const res = await api.get(`/intake/sessions/${sessionId}`);
      setCurrentSession(res.data);
      setExtractedData(res.data.extracted_data || { borrower: {}, property: {}, loan: {} });
      setDecision(res.data.decision);
      setTranscript(res.data.transcript || '');
    } catch (error) {
      toast.error('Failed to load session');
    }
  };

  const createNewSession = async () => {
    try {
      setProcessing(true);
      const res = await api.post('/intake/sessions', {
        session_type: 'new_intake'
      });
      
      // Set personality for the new session
      if (selectedPersonality) {
        await api.put(`/intake/sessions/${res.data.session_id}/personality?personality_id=${selectedPersonality.id}`);
      }
      
      setCurrentSession(res.data);
      setExtractedData(res.data.extracted_data);
      setDecision(res.data.decision);
      setTranscript('');
      setShowNewSessionDialog(false);
      toast.success(`New intake session created with ${selectedPersonality?.name || 'default'} personality`);
      
      // Refresh sessions list
      const sessionsRes = await api.get('/intake/sessions?limit=10');
      setSessions(sessionsRes.data);
    } catch (error) {
      toast.error('Failed to create session');
    } finally {
      setProcessing(false);
    }
  };

  const setSessionPersonality = async (personalityId) => {
    if (!currentSession) return;
    
    try {
      await api.put(`/intake/sessions/${currentSession.session_id}/personality?personality_id=${personalityId}`);
      const personality = personalities.find(p => p.id === personalityId);
      setSelectedPersonality(personality);
      toast.success(`Voice agent set to ${personality?.name} (${personality?.style})`);
    } catch (error) {
      toast.error('Failed to set personality');
    }
  };

  const handleVoiceMessage = useCallback((message) => {
    console.log('Voice message received:', message);
    
    if (message.type === 'response_complete') {
      // After AI finishes speaking, extract data from the conversation
      if (message.transcript && currentSession) {
        extractDataFromTranscript();
      }
    } else if (message.type === 'error') {
      toast.error('Voice AI error: ' + (message.error?.message || 'Unknown error'));
    }
  }, [currentSession]);
  
  // Handle transcript updates from the voice client - REAL-TIME extraction
  const handleTranscriptUpdate = useCallback((fullTranscript, newText, speaker) => {
    setTranscript(fullTranscript);
    
    // Extract data in REAL-TIME as the conversation happens
    if (currentSession && fullTranscript.length > 20) {
      // Clear any pending extraction
      if (extractionTimeoutRef.current) {
        clearTimeout(extractionTimeoutRef.current);
      }
      // Extract after a short delay (500ms) to avoid too many API calls
      extractionTimeoutRef.current = setTimeout(() => {
        extractDataFromTranscriptAuto(fullTranscript);
      }, 500);
    }
  }, [currentSession]);
  
  const extractionTimeoutRef = useRef(null);
  const [isExtracting, setIsExtracting] = useState(false);
  
  // Auto-extraction in real-time (silent, updates fields as you speak)
  const extractDataFromTranscriptAuto = async (transcriptText) => {
    if (!currentSession || !transcriptText?.trim() || isExtracting) return;
    
    try {
      setIsExtracting(true);
      const res = await api.post(`/intake/sessions/${currentSession.session_id}/extract?transcript=${encodeURIComponent(transcriptText)}`);
      setExtractedData(res.data.extracted_data);
      setDecision(res.data.decision);
      console.log('Real-time extraction:', res.data.extracted_data);
    } catch (error) {
      console.error('Auto-extraction failed:', error);
    } finally {
      setIsExtracting(false);
    }
  };

  const handleStatusChange = useCallback((status) => {
    setConnectionStatus(status);
    if (status === 'connected') {
      setIsListening(true);
      toast.success('Voice connection established');
    } else if (status === 'error') {
      setIsListening(false);
      toast.error('Voice connection failed');
    } else if (status === 'disconnected') {
      setIsListening(false);
    }
  }, []);

  const startVoiceSession = async () => {
    if (!currentSession) {
      toast.error('Please create a session first');
      return;
    }
    
    if (!realtimeAvailable) {
      toast.error('Voice feature is not available. Please check OpenAI API key configuration.');
      return;
    }
    
    // Request microphone permission FIRST - this triggers the browser popup
    try {
      setConnectionStatus('connecting');
      toast.info('Please allow microphone access to start the voice session');
      
      // This will trigger the browser's "Allow/Deny" microphone popup
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // If we get here, permission was granted
      // Stop the test stream - the actual stream will be created by the audio client
      stream.getTracks().forEach(track => track.stop());
      
      toast.success('Microphone access granted!');
      
    } catch (error) {
      setConnectionStatus('disconnected');
      
      if (error.name === 'NotFoundError' || error.message?.includes('device not found')) {
        toast.error('No microphone found. Please connect a microphone and try again.');
      } else if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        toast.error('Microphone access denied. Please allow microphone access in your browser settings.');
      } else {
        toast.error('Could not access microphone: ' + error.message);
      }
      return;
    }
    
    // Now proceed with WebRTC connection
    try {
      // Get personality config for the AI
      let personalityConfig = null;
      if (selectedPersonality) {
        try {
          const configRes = await api.get(`/intake/personalities/${selectedPersonality.id}/config`);
          personalityConfig = configRes.data;
        } catch (e) {
          console.log('Using default personality config');
        }
      }
      
      audioClientRef.current = new RealtimeAudioClient(
        handleVoiceMessage, 
        handleStatusChange,
        handleTranscriptUpdate
      );
      await audioClientRef.current.init(personalityConfig);
    } catch (error) {
      let errorMessage = 'Failed to start voice session';
      if (error.message?.includes('session token')) {
        errorMessage = 'Failed to connect to voice service. Please check your connection and try again.';
      } else if (error.message?.includes('Negotiation')) {
        errorMessage = 'Failed to establish voice connection. Please try again.';
      }
      toast.error(errorMessage);
      console.error('Voice session error:', error);
    }
  };

  const stopVoiceSession = async () => {
    let finalTranscript = transcript;
    
    if (audioClientRef.current) {
      // Get the final transcript before disconnecting
      finalTranscript = audioClientRef.current.getTranscript() || transcript;
      audioClientRef.current.disconnect();
      audioClientRef.current = null;
    }
    
    setIsListening(false);
    setConnectionStatus('disconnected');
    
    // Clear any pending extraction timeout
    if (extractionTimeoutRef.current) {
      clearTimeout(extractionTimeoutRef.current);
    }
    
    // Extract final data from complete transcript
    if (finalTranscript && currentSession) {
      setTranscript(finalTranscript);
      try {
        setProcessing(true);
        const res = await api.post(`/intake/sessions/${currentSession.session_id}/extract?transcript=${encodeURIComponent(finalTranscript)}`);
        setExtractedData(res.data.extracted_data);
        setDecision(res.data.decision);
        toast.success('Conversation data extracted and saved!');
      } catch (error) {
        toast.error('Failed to extract final data');
      } finally {
        setProcessing(false);
      }
    }
  };

  const toggleMute = () => {
    if (audioClientRef.current) {
      audioClientRef.current.toggleMute(!isMuted);
      setIsMuted(!isMuted);
    }
  };

  const changeVoiceDuringSession = async (personality) => {
    if (!audioClientRef.current || !isListening) {
      toast.error('No active voice session');
      return;
    }

    try {
      // Update the session configuration with new voice
      audioClientRef.current.sendSessionConfig({
        voice: personality.voice_id,
        instructions: personality.system_prompt,
        name: personality.name
      });
      
      setSelectedPersonality(personality);
      toast.success(`Voice changed to ${personality.name}`);
    } catch (error) {
      toast.error('Failed to change voice');
      console.error('Voice change error:', error);
    }
  };

  const extractDataFromTranscript = async () => {
    if (!currentSession || !transcript.trim()) return;
    
    try {
      setProcessing(true);
      const res = await api.post(`/intake/sessions/${currentSession.session_id}/extract?transcript=${encodeURIComponent(transcript)}`);
      setExtractedData(res.data.extracted_data);
      setDecision(res.data.decision);
      toast.success('Data extracted successfully');
    } catch (error) {
      toast.error('Failed to extract data');
    } finally {
      setProcessing(false);
    }
  };

  const computeDecision = async () => {
    if (!currentSession) return;
    
    try {
      const res = await api.post(`/intake/sessions/${currentSession.session_id}/compute`);
      setDecision(res.data);
    } catch (error) {
      toast.error('Failed to compute decision');
    }
  };

  const updateFieldValue = async (section, field, value) => {
    if (!currentSession) return;
    
    const updatedData = {
      ...extractedData,
      [section]: {
        ...extractedData[section],
        [field]: value
      }
    };
    
    try {
      const res = await api.put(`/intake/sessions/${currentSession.session_id}/data`, updatedData);
      setExtractedData(res.data.extracted_data);
      setDecision(res.data.decision);
      setEditingField(null);
      toast.success('Field updated');
    } catch (error) {
      toast.error('Failed to update field');
    }
  };

  const completeSession = async () => {
    if (!currentSession) return;
    
    try {
      setProcessing(true);
      const res = await api.post(`/intake/sessions/${currentSession.session_id}/complete?create_contact=true`);
      toast.success(res.data.message);
      setCurrentSession({ ...currentSession, status: 'completed' });
      
      // Refresh sessions
      const sessionsRes = await api.get('/intake/sessions?limit=10');
      setSessions(sessionsRes.data);
    } catch (error) {
      toast.error('Failed to complete session');
    } finally {
      setProcessing(false);
    }
  };

  const cancelSession = async () => {
    if (!currentSession) return;
    
    try {
      await api.post(`/intake/sessions/${currentSession.session_id}/cancel`);
      toast.success('Session cancelled');
      setCurrentSession(null);
      setExtractedData({ borrower: {}, property: {}, loan: {} });
      setDecision(null);
      setTranscript('');
      
      // Refresh sessions
      const sessionsRes = await api.get('/intake/sessions?limit=10');
      setSessions(sessionsRes.data);
    } catch (error) {
      toast.error('Failed to cancel session');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'qualified': return 'bg-green-500';
      case 'needs_review': return 'bg-amber-500';
      case 'disqualified': return 'bg-red-500';
      case 'incomplete': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'qualified': return <CheckCircle2 className="h-5 w-5" />;
      case 'needs_review': return <AlertCircle className="h-5 w-5" />;
      case 'disqualified': return <XCircle className="h-5 w-5" />;
      default: return <AlertCircle className="h-5 w-5" />;
    }
  };

  const renderDataField = (section, field, label, type = 'text') => {
    const value = extractedData[section]?.[field];
    const isEditing = editingField === `${section}.${field}`;
    const hasValue = value !== null && value !== undefined && value !== '';
    
    return (
      <div className={`
        flex items-center justify-between py-2 border-b border-border/50 last:border-0
        ${hasValue ? 'bg-primary/5' : ''}
        transition-all duration-300
      `}>
        <span className="text-sm text-muted-foreground">{label}</span>
        {isEditing ? (
          <div className="flex items-center gap-2">
            <Input
              type={type}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="h-8 w-40"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  updateFieldValue(section, field, type === 'number' ? parseFloat(editValue) : editValue);
                } else if (e.key === 'Escape') {
                  setEditingField(null);
                }
              }}
            />
            <Button
              size="sm"
              variant="ghost"
              onClick={() => updateFieldValue(section, field, type === 'number' ? parseFloat(editValue) : editValue)}
            >
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setEditingField(null)}
            >
              <XCircle className="h-4 w-4 text-red-500" />
            </Button>
          </div>
        ) : (
          <button
            className={`
              text-sm font-medium transition-all cursor-pointer text-right
              ${hasValue 
                ? 'text-foreground hover:text-primary' 
                : 'text-muted-foreground/50 hover:text-muted-foreground italic'}
              hover:underline
            `}
            onClick={() => {
              setEditingField(`${section}.${field}`);
              setEditValue(value || '');
            }}
            title="Click to edit"
            data-testid={`field-${section}-${field}`}
          >
            {hasValue ? (
              <span className="flex items-center gap-1">
                {type === 'number' && typeof value === 'number' ? 
                  `$${value.toLocaleString()}` : 
                  String(value)}
                <span className="text-xs text-muted-foreground">(edit)</span>
              </span>
            ) : (
              'Click to add'
            )}
          </button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <main className="flex-1 ml-64 p-8">
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background" data-testid="voice-intake-page">
      <Sidebar />
      <main className="flex-1 ml-64 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Mic className="h-6 w-6 text-primary" />
              </div>
              Voice AI Intake
            </h1>
            <p className="text-muted-foreground mt-1">
              Capture client information through voice conversation
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setShowPersonalityDialog(true)}
              data-testid="personality-btn"
            >
              <UserCircle className="mr-2 h-4 w-4" />
              {selectedPersonality?.name || 'Select Agent'}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowHistoryDialog(true)}
              data-testid="view-history-btn"
            >
              <History className="mr-2 h-4 w-4" />
              History
            </Button>
            <Button
              onClick={() => setShowNewSessionDialog(true)}
              disabled={currentSession?.status === 'active'}
              data-testid="new-session-btn"
            >
              <Plus className="mr-2 h-4 w-4" />
              New Session
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-3 gap-6">
          {/* Left Column - Voice Control & Progress */}
          <div className="space-y-6">
            {/* Voice Control Card */}
            <Card className="overflow-hidden">
              <div className={`p-6 ${connectionStatus === 'connected' ? 'bg-primary/10' : 'bg-muted/50'}`}>
                <div className="flex flex-col items-center text-center">
                  {/* Voice Button */}
                  <button
                    onClick={isListening ? stopVoiceSession : startVoiceSession}
                    disabled={!currentSession || currentSession.status !== 'active' || connectionStatus === 'connecting'}
                    className={`
                      relative w-24 h-24 rounded-full transition-all duration-300
                      ${isListening 
                        ? 'bg-red-500 hover:bg-red-600 shadow-lg shadow-red-500/30' 
                        : 'bg-primary hover:bg-primary/90 shadow-lg shadow-primary/30'}
                      ${(!currentSession || currentSession.status !== 'active') ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                      flex items-center justify-center
                    `}
                    data-testid="voice-control-btn"
                  >
                    {connectionStatus === 'connecting' ? (
                      <Loader2 className="h-10 w-10 text-white animate-spin" />
                    ) : isListening ? (
                      <PhoneOff className="h-10 w-10 text-white" />
                    ) : (
                      <Phone className="h-10 w-10 text-white" />
                    )}
                    
                    {/* Pulse animation when listening */}
                    {isListening && (
                      <>
                        <span className="absolute inset-0 rounded-full animate-ping bg-red-400 opacity-30" />
                        <span className="absolute inset-0 rounded-full animate-pulse bg-red-400 opacity-20" />
                      </>
                    )}
                  </button>
                  
                  <p className="mt-4 font-medium">
                    {connectionStatus === 'connecting' ? 'Connecting...' :
                     isListening ? 'Listening... speak naturally' : 
                     currentSession?.status === 'active' ? 'Tap to Start' : 
                     'Create a session to begin'}
                  </p>
                  
                  {/* Real-time extraction indicator */}
                  {isListening && isExtracting && (
                    <div className="mt-2 flex items-center gap-2 text-sm text-primary">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      <span>Extracting data...</span>
                    </div>
                  )}
                  
                  {/* Mute Button */}
                  {isListening && (
                    <div className="flex items-center gap-2 mt-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={toggleMute}
                        data-testid="mute-btn"
                      >
                        {isMuted ? (
                          <><VolumeX className="mr-2 h-4 w-4" /> Unmute</>
                        ) : (
                          <><Volume2 className="mr-2 h-4 w-4" /> Mute</>
                        )}
                      </Button>
                      
                      {/* Voice Change Dropdown */}
                      <Select 
                        value={selectedPersonality?.id || ''} 
                        onValueChange={(id) => {
                          const personality = personalities.find(p => p.id === id);
                          if (personality) changeVoiceDuringSession(personality);
                        }}
                      >
                        <SelectTrigger className="w-40 h-8" data-testid="change-voice-select">
                          <SelectValue placeholder="Change voice" />
                        </SelectTrigger>
                        <SelectContent>
                          {personalities.map(p => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name} ({p.style})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Status Indicator */}
              <CardContent className="pt-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Connection</span>
                  <Badge 
                    variant="secondary" 
                    className={`
                      ${connectionStatus === 'connected' ? 'bg-green-500/20 text-green-600' : ''}
                      ${connectionStatus === 'connecting' ? 'bg-amber-500/20 text-amber-600' : ''}
                      ${connectionStatus === 'error' ? 'bg-red-500/20 text-red-600' : ''}
                    `}
                  >
                    {connectionStatus}
                  </Badge>
                </div>
                
                {!realtimeAvailable && (
                  <p className="text-xs text-amber-600 mt-2">
                    Voice features unavailable. Check OpenAI API key in settings.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Voice Agent Personality Card */}
            {selectedPersonality && (
              <Card className="border-primary/20 bg-primary/5">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <UserCircle className="h-4 w-4 text-primary" />
                      Voice Agent
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2"
                      onClick={() => setShowPersonalityDialog(true)}
                    >
                      <Settings2 className="h-3 w-3" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex items-center gap-3">
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center
                      ${selectedPersonality.gender === 'female' ? 'bg-pink-500/20' : 'bg-blue-500/20'}
                    `}>
                      <UserCircle className={`h-6 w-6 ${selectedPersonality.gender === 'female' ? 'text-pink-500' : 'text-blue-500'}`} />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{selectedPersonality.name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <Badge variant="secondary" className="text-xs px-1.5 py-0">
                          {selectedPersonality.gender === 'female' ? 'Female' : 'Male'}
                        </Badge>
                        <Badge 
                          variant="secondary" 
                          className={`text-xs px-1.5 py-0 ${
                            selectedPersonality.style === 'professional' 
                              ? 'bg-blue-500/20 text-blue-600' 
                              : 'bg-amber-500/20 text-amber-600'
                          }`}
                        >
                          {selectedPersonality.style === 'professional' ? 'Professional' : 'Friendly'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Progress Card */}
            {decision && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-primary" />
                    Progress
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">Data Collection</span>
                      <span className="text-sm font-medium">{decision.progress_percent}%</span>
                    </div>
                    <Progress value={decision.progress_percent} className="h-2" />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Critical Fields</span>
                    <span className="text-sm font-medium">
                      {decision.critical_fields_filled} / {decision.total_critical_fields}
                    </span>
                  </div>
                  
                  {decision.dscr_ratio && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">DSCR Ratio</span>
                      <Badge className={decision.dscr_ratio >= 1.25 ? 'bg-green-500' : decision.dscr_ratio >= 1.0 ? 'bg-amber-500' : 'bg-red-500'}>
                        {decision.dscr_ratio}
                      </Badge>
                    </div>
                  )}
                  
                  <Separator />
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Status</span>
                    <Badge className={getStatusColor(decision.qualification_status)}>
                      <span className="mr-1">{getStatusIcon(decision.qualification_status)}</span>
                      {decision.qualification_status?.replace('_', ' ').toUpperCase()}
                    </Badge>
                  </div>
                  
                  {decision.next_question_hint && (
                    <div className="p-3 bg-primary/5 rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">Next question:</p>
                      <p className="text-sm font-medium">{decision.next_question_hint}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Actions */}
            {currentSession?.status === 'active' && (
              <div className="flex flex-col gap-2">
                <Button
                  onClick={extractDataFromTranscript}
                  disabled={!transcript.trim() || processing}
                  className="w-full"
                  data-testid="extract-data-btn"
                >
                  {processing ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-2 h-4 w-4" />
                  )}
                  Extract Data from Transcript
                </Button>
                
                <Button
                  onClick={completeSession}
                  disabled={!decision?.can_proceed || processing}
                  className="w-full bg-green-600 hover:bg-green-700"
                  data-testid="complete-session-btn"
                >
                  <Save className="mr-2 h-4 w-4" />
                  Complete & Create Contact
                </Button>
                
                <Button
                  variant="outline"
                  onClick={cancelSession}
                  className="w-full text-destructive hover:text-destructive"
                  data-testid="cancel-session-btn"
                >
                  Cancel Session
                </Button>
              </div>
            )}
          </div>

          {/* Middle Column - Extracted Data */}
          <div className="space-y-6">
            {/* Edit hint banner */}
            {isListening && (
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-blue-500 flex-shrink-0" />
                <p className="text-sm text-blue-600">
                  <strong>Real-time mode:</strong> Fields fill automatically as the client speaks. Click any field to edit or correct.
                </p>
              </div>
            )}
            
            {/* Borrower Info */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="h-5 w-5 text-blue-500" />
                  Borrower Information
                </CardTitle>
                <CardDescription className="text-xs">Click any value to edit</CardDescription>
              </CardHeader>
              <CardContent>
                {renderDataField('borrower', 'first_name', 'First Name')}
                {renderDataField('borrower', 'last_name', 'Last Name')}
                {renderDataField('borrower', 'phone', 'Phone')}
                {renderDataField('borrower', 'email', 'Email', 'email')}
                {renderDataField('borrower', 'citizenship', 'Citizenship')}
                {renderDataField('borrower', 'credit_score', 'Credit Score', 'number')}
              </CardContent>
            </Card>

            {/* Property Info */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-emerald-500" />
                  Property Information
                </CardTitle>
                <CardDescription className="text-xs">Click any value to edit</CardDescription>
              </CardHeader>
              <CardContent>
                {renderDataField('property', 'address', 'Address')}
                {renderDataField('property', 'city', 'City')}
                {renderDataField('property', 'state', 'State')}
                {renderDataField('property', 'zip_code', 'ZIP Code')}
                {renderDataField('property', 'property_type', 'Property Type')}
                {renderDataField('property', 'purchase_price', 'Purchase Price', 'number')}
                {renderDataField('property', 'estimated_rent', 'Est. Monthly Rent', 'number')}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Loan Info & Transcript */}
          <div className="space-y-6">
            {/* Loan Info */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-amber-500" />
                  Loan Information
                </CardTitle>
                <CardDescription className="text-xs">Click any value to edit</CardDescription>
              </CardHeader>
              <CardContent>
                {renderDataField('loan', 'loan_type', 'Loan Type')}
                {renderDataField('loan', 'loan_amount', 'Loan Amount', 'number')}
                {renderDataField('loan', 'down_payment_percent', 'Down Payment %', 'number')}
                {renderDataField('loan', 'timeline', 'Timeline')}
              </CardContent>
            </Card>

            {/* Transcript */}
            <Card className="flex-1">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>Conversation Transcript</span>
                  {isListening && (
                    <Badge variant="secondary" className="bg-green-500/20 text-green-600">
                      <span className="mr-1 h-2 w-2 rounded-full bg-green-500 animate-pulse inline-block" />
                      Live
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-64">
                  {transcript ? (
                    <p className="text-sm whitespace-pre-wrap">{transcript}</p>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Mic className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>Start a voice session to see the transcript here</p>
                    </div>
                  )}
                </ScrollArea>
                
                {/* Manual transcript input for testing */}
                <div className="mt-4 space-y-2">
                  <Label className="text-xs text-muted-foreground">Manual input (for testing)</Label>
                  <textarea
                    className="w-full h-20 p-2 text-sm border rounded-lg resize-none bg-muted/50"
                    placeholder="Type or paste transcript here..."
                    value={transcript}
                    onChange={(e) => setTranscript(e.target.value)}
                    data-testid="transcript-input"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* New Session Dialog */}
        <Dialog open={showNewSessionDialog} onOpenChange={setShowNewSessionDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Start New Intake Session</DialogTitle>
              <DialogDescription>
                Begin a new voice intake session to capture client information.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-muted-foreground">
                A new session will be created where you can:
              </p>
              <ul className="mt-2 space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  Capture client info through voice conversation
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  Automatically extract structured data
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  Get real-time qualification status
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  Create a contact when complete
                </li>
              </ul>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewSessionDialog(false)}>
                Cancel
              </Button>
              <Button onClick={createNewSession} disabled={processing}>
                {processing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="mr-2 h-4 w-4" />
                )}
                Create Session
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* History Dialog */}
        <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Session History</DialogTitle>
              <DialogDescription>
                View and manage your intake sessions
              </DialogDescription>
            </DialogHeader>
            <ScrollArea className="h-96">
              <div className="space-y-2">
                {sessions.length > 0 ? (
                  sessions.map((session) => (
                    <div
                      key={session.session_id}
                      className="p-4 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => {
                        loadSession(session.session_id);
                        setShowHistoryDialog(false);
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{session.session_id}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(session.created_at).toLocaleString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">
                            {session.progress_percent}% complete
                          </Badge>
                          <Badge className={
                            session.status === 'active' ? 'bg-blue-500' :
                            session.status === 'completed' ? 'bg-green-500' : 'bg-gray-500'
                          }>
                            {session.status}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No sessions yet</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>

        {/* Personality Selection Dialog */}
        <Dialog open={showPersonalityDialog} onOpenChange={setShowPersonalityDialog}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Choose Voice Agent Personality</DialogTitle>
              <DialogDescription>
                Select the voice and communication style for your AI intake assistant
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="grid grid-cols-2 gap-4">
                {personalities.map((personality) => (
                  <div
                    key={personality.id}
                    className={`
                      p-4 rounded-lg border-2 cursor-pointer transition-all
                      ${selectedPersonality?.id === personality.id 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-primary/50 hover:bg-muted/50'}
                    `}
                    onClick={() => {
                      setSelectedPersonality(personality);
                      if (currentSession?.status === 'active') {
                        setSessionPersonality(personality.id);
                      }
                    }}
                    data-testid={`personality-${personality.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`
                        w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0
                        ${personality.gender === 'female' ? 'bg-pink-500/20' : 'bg-blue-500/20'}
                      `}>
                        <UserCircle className={`h-7 w-7 ${personality.gender === 'female' ? 'text-pink-500' : 'text-blue-500'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold">{personality.name}</h4>
                          {selectedPersonality?.id === personality.id && (
                            <CheckCircle2 className="h-4 w-4 text-primary" />
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 mb-2">
                          <Badge variant="secondary" className="text-xs">
                            {personality.gender === 'female' ? 'Female' : 'Male'}
                          </Badge>
                          <Badge 
                            variant="secondary" 
                            className={`text-xs ${
                              personality.style === 'professional' 
                                ? 'bg-blue-500/20 text-blue-600' 
                                : 'bg-amber-500/20 text-amber-600'
                            }`}
                          >
                            {personality.style === 'professional' ? (
                              <><Briefcase className="h-3 w-3 mr-1" />Professional</>
                            ) : (
                              <><Heart className="h-3 w-3 mr-1" />Friendly</>
                            )}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {personality.description}
                        </p>
                      </div>
                    </div>
                    
                    {/* Sample greeting */}
                    <div className="mt-3 p-2 bg-muted/50 rounded text-xs italic text-muted-foreground">
                      "{personality.greeting?.substring(0, 80)}..."
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPersonalityDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default VoiceIntakePage;
